import { Module, PATH_DICTS_SERVICES_AND_GOODS } from "@/constants";
import { fetchServicesAndGoodsList } from "@/services/servicesAndGoods";
import { ExternalLink } from "lucide-react";
import { forwardRef, useCallback } from "react";
import { ControllerRenderProps, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option } from "./AsyncSelect";
import appConfiguration from "./cfg/AppCfg";

import { Button } from "@/components/ui/button";
import { SuccessFetchListResponse } from "@/services/api";
import { ReadServicesAndGoods } from "@/types/servicesAndGoods";
import { UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router-dom";

interface ServicesAndGoodsSelectProps extends ControllerRenderProps {
  className?: string;
  inDialog?: boolean;
  clear?: boolean;
  showLink?: boolean;
  placeholder?: string;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, ServicesAndGoodsSelectProps>(
  function ServicesAndGoodsSelect(
    { value, inDialog, showLink = true, defaultItem, ...props },
    ref,
  ) {
    const { t } = useTranslation();

    const selectedServicesAndGoods = useWatch({
      name: "services_and_goods_id",
    });

    const getSelectedServicesAndGoodsData = useCallback(
      (query: UseQueryResult<SuccessFetchListResponse<Option>, Error>) => {
        const data = query?.data;
        if (!selectedServicesAndGoods || !data?.items) {
          return;
        }
        return data.items.find((item) => item.id === selectedServicesAndGoods);
      },
      [selectedServicesAndGoods],
    );

    const renderServicesAndGoodsName = useCallback(
      (item: ReadServicesAndGoods) => (
        <span className="flex items-center gap-1">
          <span className="font-mono text-xs">
            {item.code ?? defaultItem?.code}
          </span>
          <span className="text-gray-300">|</span>
          <span>{item.name}</span>
        </span>
      ),
      [defaultItem],
    );

    return (
      <span className="flex items-start gap-1">
        <span className="flex-1">
          <AsyncSelect
            queryKey={[Module.ServicesAndGoods]}
            queryFn={() =>
              fetchServicesAndGoodsList({
                limit: appConfiguration.selectComponentsPaginationLimit,
              })
            }
            placeholder={t("servicesAndGoodsSelect.placeholder")}
            loadingLabel={t("servicesAndGoodsSelect.loading")}
            noResultsLabel={t("servicesAndGoodsSelect.noResults")}
            searchLabel={t("servicesAndGoodsSelect.search")}
            enableFilter={false}
            renderName={renderServicesAndGoodsName}
            className="w-full"
            {...props}
            value={value}
            defaultItem={defaultItem}
            ref={ref}
            inDialog={inDialog}
            footer={(query) => {
              const selectedServicesAndGoodsData =
                getSelectedServicesAndGoodsData(query);
              if (
                !selectedServicesAndGoodsData &&
                !defaultItem?.invoice_description
              ) {
                return null;
              }
              return (
                <div className="mt-1 text-xs text-gray-500">
                  <span className="font-medium">
                    {t("servicesAndGoods.fields.invoice_description")}:
                  </span>{" "}
                  <span className="truncate">
                    {String(
                      selectedServicesAndGoodsData?.invoice_description ??
                        defaultItem?.invoice_description ??
                        t("servicesAndGoods.fields.noDescription"),
                    )}
                  </span>
                </div>
              );
            }}
          />
        </span>
        {showLink && (
          <Link to={PATH_DICTS_SERVICES_AND_GOODS}>
            <Button variant="ghost" size="icon" type="button">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </Link>
        )}
      </span>
    );
  },
);
